import { Delete, Edit } from "@mui/icons-material"
import { Box, Divider, IconButton, Typography } from "@mui/material"
import { MiniGalery } from "components/MiniGalery.tsx/MiniGalery"
import { Device, DeviceSetup } from "graphql/types"
import { useMemo } from "react"
import { isDeviceAux, isDeviceConsumable, isDeviceEut, isDeviceTe } from "store/typeGuards"

type DeviceScanListProps = {
  devices: Device[]
  deviceSetup: DeviceSetup[]
  // eslint-disable-next-line no-unused-vars
  setDeviceSetup: (arg: DeviceSetup[]) => void
  scannedBarcodes?: string[]
  // eslint-disable-next-line no-unused-vars
  onEditBarcode?: (barcode: string) => void
  // eslint-disable-next-line no-unused-vars
  onDeleteBarcode?: (barcode: string) => void
}

export const DeviceScanList = (props: DeviceScanListProps) => {
  const {
    devices,
    deviceSetup,
    setDeviceSetup,
    scannedBarcodes = [],
    onEditBarcode,
    onDeleteBarcode,
  } = props

  const handleDeleteSetup = (barcode: string) => {
    const updated = deviceSetup
      .filter((item) => item.device.barcode !== barcode)
      .map((item) => ({
        ...item,
        linkedTo: (item.linkedTo || []).filter((link) => link?.device.barcode !== barcode),
      }))
    setDeviceSetup(updated)
    // Notify parent component to remove from scannedBarcodes
    if (onDeleteBarcode) {
      onDeleteBarcode(barcode)
    }
  }

  const getDeviceSetup = useMemo(
    () => (barcode: string) => {
      const returnValue = deviceSetup.find((item) => item.device.barcode === barcode)
      if (returnValue) return returnValue
      return null
    },
    [deviceSetup]
  )

  const getDeviceLinkedTo = useMemo(
    () => (barcode: string) => {
      return deviceSetup
        .flatMap((ds) => ds.linkedTo ?? []) // Extrahiere alle linkedTo-Elemente
        .find((link) => link?.device.barcode === barcode)?.device // Finde das gesuchte linkedTo-Device
    },
    [deviceSetup]
  )

  return (
    <>
      {/* EUT */}
      {devices
        .filter((item) => isDeviceEut(item))
        .map((deviceItem) => {
          const itemDeviceSetup = getDeviceSetup(deviceItem.barcode)

          // Consider EUT scanned/added if it has a DeviceSetup entry
          const isGreen = !!itemDeviceSetup || scannedBarcodes.includes(deviceItem.barcode)

          return (
            <Box key={deviceItem.id}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography variant="body2" color={isGreen ? "green" : "white"}>
                  {deviceItem.barcode}: {deviceItem.name}
                </Typography>
                {onEditBarcode && itemDeviceSetup && (
                  <IconButton
                    size="small"
                    onClick={() => onEditBarcode(deviceItem.barcode)}
                    sx={{ color: "white", p: 0.25 }}
                    aria-label="Bearbeiten"
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                )}
                {itemDeviceSetup && (
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteSetup(deviceItem.barcode)}
                    sx={{ color: "white", p: 0.25 }}
                    aria-label="Löschen"
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                )}
              </Box>
              <Box sx={{ ml: 2 }}>
                {itemDeviceSetup && itemDeviceSetup.note !== "" && (
                  <Typography variant="body2">Anmerkung: {itemDeviceSetup.note}</Typography>
                )}

                {itemDeviceSetup &&
                  itemDeviceSetup.linkedTo &&
                  itemDeviceSetup.linkedTo.length > 0 && (
                    <Typography variant="body2">
                      Verbunden:{" "}
                      {itemDeviceSetup.linkedTo
                        .map((l) => l?.device?.barcode)
                        .filter(Boolean)
                        .join(", ")}
                    </Typography>
                  )}

                {itemDeviceSetup &&
                  itemDeviceSetup.linkedTo &&
                  itemDeviceSetup.linkedTo.map((item) => (
                    <Box key={item?.device.id}>
                      <Typography variant="body2">
                        - Verknüpft mit {item?.device.barcode}: {item?.device.name}{" "}
                        {item?.note !== "" ? `(${item?.note})` : ""}
                      </Typography>
                    </Box>
                  ))}

                {itemDeviceSetup && itemDeviceSetup.images.length > 0 && (
                  <MiniGalery images={itemDeviceSetup.images} deleteButton={false} />
                )}
              </Box>
              <Divider />
            </Box>
          )
        })}

      {/* TEST EQUIPMENT */}
      {devices
        .filter((item) => isDeviceTe(item))
        .map((deviceItem) => {
          const itemDeviceSetup = getDeviceSetup(deviceItem.barcode)
          const itemLinkedTo = getDeviceLinkedTo(deviceItem.barcode)

          return (
            <Box key={deviceItem.id}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography
                  variant="body2"
                  color={itemDeviceSetup || itemLinkedTo ? "green" : "white"}
                >
                  {deviceItem.barcode}: {deviceItem.name}
                </Typography>
                {onEditBarcode && itemDeviceSetup && (
                  <IconButton
                    size="small"
                    onClick={() => onEditBarcode(deviceItem.barcode)}
                    sx={{ color: "white", p: 0.25 }}
                    aria-label="Bearbeiten"
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                )}
                {itemDeviceSetup && (
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteSetup(deviceItem.barcode)}
                    sx={{ color: "white", p: 0.25 }}
                    aria-label="Löschen"
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                )}
              </Box>
              <Box sx={{ ml: 2 }}>
                {itemDeviceSetup && itemDeviceSetup.note !== "" && (
                  <Typography variant="body2">Anmerkung: {itemDeviceSetup.note}</Typography>
                )}
                {itemDeviceSetup && itemDeviceSetup.images.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      flexWrap: "wrap",
                    }}
                  >
                    {itemDeviceSetup && itemDeviceSetup.images.length > 0 && (
                      <MiniGalery images={itemDeviceSetup.images} deleteButton={false} />
                    )}
                  </Box>
                )}
              </Box>
              <Divider />
            </Box>
          )
        })}

      {/* TEST CONSUMABLES */}
      {devices
        .filter((item) => isDeviceConsumable(item))
        .map((deviceItem) => {
          const itemDeviceSetup = getDeviceSetup(deviceItem.barcode)
          const itemLinkedTo = getDeviceLinkedTo(deviceItem.barcode)
          if (itemLinkedTo) return null
          return (
            <Box key={deviceItem.id}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography
                  variant="body2"
                  color={itemDeviceSetup || itemLinkedTo ? "green" : "white"}
                >
                  {deviceItem.barcode}: {deviceItem.name}
                </Typography>
                {onEditBarcode && itemDeviceSetup && (
                  <IconButton
                    size="small"
                    onClick={() => onEditBarcode(deviceItem.barcode)}
                    sx={{ color: "white", p: 0.25 }}
                    aria-label="Bearbeiten"
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                )}
                {itemDeviceSetup && (
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteSetup(deviceItem.barcode)}
                    sx={{ color: "white", p: 0.25 }}
                    aria-label="Löschen"
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                )}
              </Box>

              <Box sx={{ ml: 2 }}>
                {itemDeviceSetup && itemDeviceSetup.note !== "" && (
                  <Typography variant="body2">Anmerkung: {itemDeviceSetup.note}</Typography>
                )}

                {itemDeviceSetup && itemDeviceSetup.images.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      flexWrap: "wrap",
                    }}
                  >
                    {itemDeviceSetup && itemDeviceSetup.images.length > 0 && (
                      <MiniGalery images={itemDeviceSetup.images} deleteButton={false} />
                    )}
                  </Box>
                )}
              </Box>

              <Divider />
            </Box>
          )
        })}

      {/* AUX */}
      {devices
        .filter((item) => isDeviceAux(item))
        .map((deviceItem) => {
          const itemDeviceSetup = getDeviceSetup(deviceItem.barcode)
          return (
            <Box key={deviceItem.id}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography variant="body2" color={itemDeviceSetup ? "green" : "white"}>
                  {deviceItem.barcode}: {deviceItem.name}
                </Typography>
                {onEditBarcode && itemDeviceSetup && (
                  <IconButton
                    size="small"
                    onClick={() => onEditBarcode(deviceItem.barcode)}
                    sx={{ color: "white", p: 0.25 }}
                    aria-label="Bearbeiten"
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                )}
                {itemDeviceSetup && (
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteSetup(deviceItem.barcode)}
                    sx={{ color: "white", p: 0.25 }}
                    aria-label="Löschen"
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                )}
              </Box>
              <Box sx={{ ml: 2 }}>
                {itemDeviceSetup && itemDeviceSetup.note !== "" && (
                  <Typography variant="body2">Anmerkung: {itemDeviceSetup.note}</Typography>
                )}
                {itemDeviceSetup && itemDeviceSetup.images.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      flexWrap: "wrap",
                    }}
                  >
                    {itemDeviceSetup && itemDeviceSetup.images.length > 0 && (
                      <MiniGalery images={itemDeviceSetup.images} deleteButton={false} />
                    )}
                  </Box>
                )}
              </Box>
              <Divider />
            </Box>
          )
        })}
    </>
  )
}
